import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { get } from "lodash";

// Get API base URL from environment variables
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://dev-socket.pullseai.com/api';

/*
This class is used to manage the token for the API client.
This piece of code is used to handle the refresh token logic.
Tokens are sourced from Better Auth and injected into requests on demand.
setGetTokenFunction is used to set the getToken function (and skip the token caching logic).
getToken is used to get the token from the getTokenFunction.
*/
class TokenManager {
    private static getTokenFunction: (() => Promise<string | null>) | null = null;

    static setGetTokenFunction(getToken: () => Promise<string | null>) {
        this.getTokenFunction = getToken;
    }

    static async getToken(): Promise<string | null> {
        if (this.getTokenFunction) {
            return await this.getTokenFunction()
        }
    }
}

// Create the main API client
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
});

// Request interceptor - Adds token and workspace/client IDs
const requestInterceptor = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    try {
        // Get token from cache/manager
        const token = await TokenManager.getToken();
        if (token) {
            config.headers.set("Authorization", `Bearer ${token}`);
        } else {
            console.warn(`No token available for request to: ${config.url}`);
        }

        // Get workspace_id and client_id from localStorage
        const workspaceId = localStorage.getItem("workspaceId");
        const clientId = localStorage.getItem("clientId");
        if (workspaceId) {
            config.headers.set("X-Workspace-Id", workspaceId);
        }

        // Add params instead of modifying URL
        if (workspaceId || clientId) {
            if (!config.params) {
                config.params = {};
            }

            if (workspaceId && !config.params.workspace_id) {
                config.params.workspace_id = workspaceId;
            }

            if (clientId && !config.params.client_id) {
                config.params.client_id = clientId;
            }
        }
    } catch (error) {
        console.error("Error in request interceptor:", error);
    }

    return config;
};

const requestErrorInterceptor = (error: any) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
};

// Response interceptor - Handles successful responses and errors
const responseInterceptor = (response: AxiosResponse) => {
    return response;
};

const responseErrorInterceptor = async (error: any) => {
    const status = error.response?.status;
    const errorMessage = get(error, "response.data.message", "Unknown error occurred");
    const errorCode = get(error, "response.data.code", "");
    const requestUrl = error.config?.url;
    const timeout = error.code === 'ECONNABORTED';

    // Handle 401 Unauthorized - try to refresh token and retry once
    if (status === 401 && !error.config._retry) {
        console.log('401 Unauthorized - attempting token refresh and retry');

        // Mark this request as retried
        error.config._retry = true;

        try {
            // Get fresh token
            const newToken = await TokenManager.getToken();
            if (newToken) {
                // Retry the original request with new token
                error.config.headers.set("Authorization", `Bearer ${newToken}`);
                return axios(error.config);
            }
        } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
        }
    }

    if (timeout) {
        console.error(`API Timeout after ${error.config?.timeout || 'unknown'}ms: ${requestUrl}`);
        return Promise.reject({
            message: "Request timed out. The server is taking too long to respond.",
            isTimeoutError: true,
            originalError: error
        });
    }

    console.error(`API Error: ${status || 'network'} ${errorMessage} (${errorCode}) on ${requestUrl}`);

    // Network errors
    if (!navigator.onLine || error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        console.log('Network error detected - server might be unavailable');
        return Promise.reject({
            message: "Cannot connect to the server. Please check your network connection and try again later.",
            isOfflineError: true,
            originalError: error
        });
    }

    // CORS errors
    if (error.message.includes('CORS')) {
        console.error('CORS error detected:', error.message);
        return Promise.reject({
            message: "There was a CORS error. This usually means the API server is not configured to accept requests from this origin.",
            isCorsError: true,
            originalError: error
        });
    }

    // Handle 404 Not Found errors
    if (status === 404) {
        return Promise.reject({
            message: "The requested resource was not found.",
            isNotFoundError: true,
            originalError: error
        });
    }

    // Handle 500 Internal Server errors
    if (status >= 500) {
        return Promise.reject({
            message: "Server error. Please try again later.",
            isServerError: true,
            originalError: error
        });
    }

    return Promise.reject({
        message: errorMessage || "An error occurred while communicating with the server.",
        originalError: error
    });
};

// Initialize interceptors for all clients
const initializeInterceptors = () => {
    console.log('Initializing API client with base URL:', API_BASE_URL);

    // Add interceptors to main API client
    apiClient.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
    apiClient.interceptors.response.use(responseInterceptor, responseErrorInterceptor);
};

// Initialize interceptors immediately
initializeInterceptors();

// Set Axios default config (legacy support)
const setAxiosDefaultConfig = (): void => {
    axios.defaults.baseURL = API_BASE_URL;
    axios.defaults.headers.post["Content-Type"] = "application/json";
};

// Check if client is offline
const isOffline = (): boolean => {
    return !navigator.onLine;
};

// Main HttpClient class (for backward compatibility)
export class HttpClient {
    static apiClient = apiClient;
    static setAxiosDefaultConfig = setAxiosDefaultConfig;
    static isOffline = isOffline;
    static initializeInterceptors = initializeInterceptors;

    /**
     * Expose the current auth token so non-Axios flows (e.g., SSE/fetch) can authenticate
     */
    static async getAuthToken(): Promise<string | null> {
        return TokenManager.getToken();
    }

    // Set the getToken function for token management
    static setGetTokenFunction(getToken: () => Promise<string | null>) {
        TokenManager.setGetTokenFunction(getToken);
    }
}

// Export individual clients for backward compatibility
export { apiClient, setAxiosDefaultConfig };

// Export interceptors for backward compatibility
export {
    requestInterceptor,
    requestErrorInterceptor,
    responseInterceptor,
    responseErrorInterceptor
};

// Default export for backward compatibility
export default HttpClient;
